import { useCallback, useRef, useEffect } from "react";
import { isLastFieldInForm } from "../utils/isLastFieldInForm";
import {
  UseSelectKeyboardParams,
  navigateToNextField,
  submitForm,
} from "./useSelectKeyboard.helpers";
import {
  useCleanupRefs,
  useResolvePostBlurAction,
  useHandleNavigationAfterBlur,
  useHandleEnterWithOptions,
  useHandleEnterWithSelectedValue,
  useHandleEnterDefault,
  useHandleEnterKey,
  useHandleArrowKeyNavigation,
  useHandleSearchKeyDown,
  useHandleKeyDown,
} from "./useSelectKeyboard.hooks";

export type { UseSelectKeyboardParams } from "./useSelectKeyboard.helpers";

/**
 * Builds the trio of Enter-key handlers (options selection, existing-value
 * selection, and default submit/navigate) for the select keyboard hook.
 *
 * @param params The select keyboard configuration and callbacks.
 * @param navigationTimeoutRef Ref holding the pending navigation timeout id.
 * @param blurTimeoutRef Ref holding the pending blur timeout id.
 * @param hasKeyboardNavigationRef Ref tracking prior keyboard navigation.
 * @returns `{ options, selected, fallback }` Enter handlers.
 */
function useEnterHandlers(params: UseSelectKeyboardParams, navigationTimeoutRef: React.MutableRefObject<number | null>, blurTimeoutRef: React.MutableRefObject<number | null>, hasKeyboardNavigationRef: React.MutableRefObject<boolean>) {
  const resolvePostBlurAction = useResolvePostBlurAction(params.onNextField, params.name, params.onSubmit, navigationTimeoutRef);
  const handleNavigationAfterBlur = useHandleNavigationAfterBlur(blurTimeoutRef, resolvePostBlurAction);
  const shouldSkipSelection = useCallback(
    () => !params.selectedValue && !params.query && !hasKeyboardNavigationRef.current,
    [params.selectedValue, params.query],
  );
  return {
    options: useHandleEnterWithOptions(shouldSkipSelection, params.filteredOptions, params.activeOptionIndex, params.ensureDisplayNameInQuery, params.onSelect, handleNavigationAfterBlur),
    selected: useHandleEnterWithSelectedValue(params.selectedValue, params.ensureDisplayNameInQuery, params.onSelect, handleNavigationAfterBlur),
    fallback: useHandleEnterDefault(params.name, params.onSubmit, params.onNextField, navigationTimeoutRef),
  };
}

/**
 * Composes the Enter-key handler, dispatching between option selection,
 * existing-value selection, and the default fallback based on current state.
 *
 * @param params The select keyboard configuration and callbacks.
 * @param navigationTimeoutRef Ref holding the pending navigation timeout id.
 * @param blurTimeoutRef Ref holding the pending blur timeout id.
 * @param hasKeyboardNavigationRef Ref tracking prior keyboard navigation.
 * @returns The composed Enter-key handler.
 */
function useSelectKeyboardEnter(params: UseSelectKeyboardParams, navigationTimeoutRef: React.MutableRefObject<number | null>, blurTimeoutRef: React.MutableRefObject<number | null>, hasKeyboardNavigationRef: React.MutableRefObject<boolean>) {
  const handlers = useEnterHandlers(params, navigationTimeoutRef, blurTimeoutRef, hasKeyboardNavigationRef);
  const shouldSelectFromOptions = useCallback(
    () => params.enableEnterKeySelection && params.isFocused && params.filteredOptions.length > 0,
    [params.enableEnterKeySelection, params.isFocused, params.filteredOptions.length],
  );
  const shouldSelectExistingValue = useCallback(
    () => params.enableEnterKeySelection && params.isFocused && !!params.selectedValue,
    [params.enableEnterKeySelection, params.isFocused, params.selectedValue],
  );
  return useHandleEnterKey(shouldSelectFromOptions, shouldSelectExistingValue, handlers.options, handlers.selected, handlers.fallback);
}

/**
 * Top-level keyboard hook for the Select: wires Enter, arrow navigation, and
 * search-input keydown handling, plus a keyboard-navigation reset.
 *
 * @param params The select keyboard configuration and callbacks.
 * @returns `{ handleKeyDown, handleSearchKeyDown, resetKeyboardNavigation }`.
 */
export function useSelectKeyboard(params: UseSelectKeyboardParams) {
  const navigationTimeoutRef = useRef<number | null>(null);
  const blurTimeoutRef = useRef<number | null>(null);
  const hasKeyboardNavigationRef = useRef(false);

  useCleanupRefs(navigationTimeoutRef, blurTimeoutRef);

  const resetKeyboardNavigation = useCallback(() => { hasKeyboardNavigationRef.current = false; }, []);
  const handleEnterKey = useSelectKeyboardEnter(params, navigationTimeoutRef, blurTimeoutRef, hasKeyboardNavigationRef);

  const handleArrowKeyNavigation = useHandleArrowKeyNavigation(
    params.filteredOptions, params.setActiveOptionIndex, hasKeyboardNavigationRef,
  );
  const handleSearchKeyDown = useHandleSearchKeyDown(
    params.filteredData, params.activeOptionIndex, params.close, params.onSelect, params.setActiveOptionIndex,
  );
  const handleKeyDown = useHandleKeyDown(
    handleEnterKey, params.isFocused, params.filteredOptions.length, handleArrowKeyNavigation,
  );

  return { handleKeyDown, handleSearchKeyDown, resetKeyboardNavigation };
}
